import {
  useMutationCreateUser,
  useMutationUpdateUser,
  useQueryGetOneUser,
  Users,
  usersQueryKey,
} from "@apis:user/user";
import { SuperDialog } from "@components:SuperDialog";
import { SuperInput } from "@components:SuperInput/SuperInput";
import { useInvalidateQueries } from "@hooks:useInvalidateQueries";
import { Box, MenuItem } from "@mui/material";
import { useToasterStoreActions } from "@stores:global";
import { omitEmpty } from "@utils:object";
import { Controller, useForm } from "react-hook-form";

import { FeatureFlag } from "@:flag";
import { UserRoles } from "@:rbac";

interface UserModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string;
}

export const UserModal = ({ isOpen, onClose, userId }: UserModalProps) => {
  const invalidateQueries = useInvalidateQueries();
  const toastActions = useToasterStoreActions();
  const { data, isLoading } = useQueryGetOneUser(userId!, {
    enabled: !!userId,
  });

  const { register, handleSubmit, formState, control } = useForm({
    defaultValues: {
      name: "",
      enabled: true,
      email: "",
      password: "",
      role: UserRoles.USER,
    },
    values: {
      name: data?.name,
      enabled: data?.enabled,
      email: data?.email,
      password: "",
      role: data?.role,
    },
    // we need to use mode: 'all' for MUI https://github.com/orgs/react-hook-form/discussions/2357#discussioncomment-42073
    mode: "all",
  });

  const mutationCreateUser = useMutationCreateUser({
    onSuccess: async () => {
      toastActions.setToaster({
        open: true,
        message: "User created",
        variant: "success",
      });
      await invalidateQueries.run([
        {
          queryKey: usersQueryKey.getAll.queryKey,
        },
      ]);
      onClose();
    },
    onError: () => {
      toastActions.setToaster({
        open: true,
        message: "Failed to create user",
        variant: "error",
      });
    },
  });

  const mutationUpdateUser = useMutationUpdateUser({
    onSuccess: async () => {
      toastActions.setToaster({
        open: true,
        message: "User updated",
        variant: "success",
      });
      await invalidateQueries.run([
        {
          queryKey: usersQueryKey._def,
        },
      ]);
      onClose();
    },
    onError: () => {
      toastActions.setToaster({
        open: true,
        message: "Failed to update user",
        variant: "error",
      });
    },
  });

  return (
    <SuperDialog
      open={isOpen}
      onClose={onClose}
      title={userId ? "Edit User" : "Add User"}
      isLoading={isLoading}
      sx={{
        "& .MuiDialog-paper": {
          height: "34rem",
          width: "30rem",
        },
      }}
      actionControls={{
        secondary: {
          label: "Cancel",
          onClick: () => {
            onClose();
          },
          sx: {
            width: "5rem",
          },
        },
        primary: {
          sx: {
            width: "5rem",
          },
          label: "Save",
          onClick: async () => {
            await handleSubmit((data) => {
              if (userId) {
                mutationUpdateUser.mutate({
                  id: userId,
                  ...omitEmpty(data),
                });
              } else {
                mutationCreateUser.mutate({
                  name: data.name!,
                  email: data.email!,
                  password: data.password!,
                  role: data.role as Users["POST"]["RequestBody"]["role"],
                  ...(FeatureFlag.UpdateEnabled
                    ? { enabled: data.enabled! }
                    : {}),
                });
              }
            })();
          },
          disabled: !formState.isValid,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <SuperInput.TextField
          required
          label="Name"
          {...register("name", {
            required: "Name is required",
          })}
          errorText={formState.errors.name?.message}
        />
        <SuperInput.TextField
          required
          label="Email"
          {...register("email", {
            required: "Email is required",
            pattern: {
              value: /\S+@\S+\.\S+/,
              message: "Entered value does not match email format",
            },
          })}
          errorText={formState.errors.email?.message}
          type="email"
        />
        <SuperInput.TextField
          required
          label="Password"
          {...register("password", {
            ...(userId
              ? undefined
              : {
                  required: "Password is required",
                }),
          })}
          errorText={formState.errors.password?.message}
          type="password"
        />

        <Box
          sx={{
            display: "flex",
            gap: "3rem",
            alignItems: "center",
          }}
        >
          <Controller
            name="role"
            defaultValue={UserRoles.USER}
            rules={{
              required: "Role is required",
            }}
            control={control}
            render={({ field }) => (
              <SuperInput.Select
                required
                sx={{
                  flex: 1,
                }}
                {...field}
                width="100%"
                label="Role"
                errorText={formState.errors.role?.message}
              >
                <MenuItem value={UserRoles.USER}>{UserRoles.USER}</MenuItem>
                <MenuItem value={UserRoles.ADMINISTRATOR}>
                  {UserRoles.ADMINISTRATOR}
                </MenuItem>
              </SuperInput.Select>
            )}
          />
          {FeatureFlag.UpdateEnabled && (
            <Controller
              name="enabled"
              defaultValue={true}
              control={control}
              render={({ field }) => (
                <SuperInput.Switch
                  required
                  sx={{
                    marginTop: "1rem",
                  }}
                  label="Enabled"
                  checked={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          )}
        </Box>
      </Box>
    </SuperDialog>
  );
};
